import { TextField } from 'formik-mui'
import ContentSaveIcon from 'mdi-react/ContentSaveIcon'
import EditIcon from 'mdi-react/EditIcon'
import KeyboardReturnIcon from 'mdi-react/KeyboardReturnIcon'
import React from 'react'
import { SelectFormik } from 'scenes/Dashboard/components/Inputs/Select'
import { Deal } from 'scenes/Dashboard/data/deals/types'
import { OrganizationSourceOptions } from 'scenes/Dashboard/data/organizations/types'
import { FastField, FormHelpers, FormManager, FormProps } from 'utils-next/forms'
import * as Yup from 'yup'

import { createStyles, IconButton, Theme, Typography } from '@mui/material'
import { WithStyles } from '@mui/styles'

import { DealInfoFormData } from './DealInfoForm.data'

export class DealInfo extends React.PureComponent<DealInfoPropsStyled, DealInfoState> {
  state = {
    edit: false,
  }
  constructor(props: DealInfoPropsStyled) {
    super(props)
    this.renderData = this.renderData.bind(this)
    this.renderForm = this.renderForm.bind(this)
    this.renderInternalForm = this.renderInternalForm.bind(this)
    this.handleEditOpen = this.handleEditOpen.bind(this)
    this.handleEditClose = this.handleEditClose.bind(this)
    this.handleSubmit = this.handleSubmit.bind(this)
  }

  handleEditOpen() {
    this.setState({
      edit: true,
    })
  }

  handleEditClose() {
    this.setState({
      edit: false,
    })
  }

  handleSubmit(values: DealInfoFormData, actions: FormHelpers<DealInfoFormData>) {
    const { dealId, onSubmit } = this.props
    onSubmit(dealId!, values)
    this.handleEditClose()
    actions.setSubmitting(false)
  }

  renderData() {
    const { notes, receivedOn, source, classes } = this.props
    return (
      <div>
        <div className={classes.actionHandlerTop}>
          <IconButton onClick={this.handleEditOpen}>
            <EditIcon className={classes.actionIcon} />
          </IconButton>
        </div>
        <div className={classes.fieldWrapper}>
          <div className={classes.fieldSection}>
            <Typography variant="subtitle1" className={classes.fieldSectionName}>
              Deal received on
            </Typography>
            <Typography variant="body2">{receivedOn}</Typography>
          </div>
          <div className={classes.fieldSection}>
            <Typography variant="subtitle1" className={classes.fieldSectionName}>
              Notes
            </Typography>
            <Typography variant="body2" className={classes.notes}>
              {notes || 'No notes'}
            </Typography>
          </div>
          <div className={classes.fieldSection}>
            <Typography variant="subtitle1" className={classes.fieldSectionName}>
              Deal source
            </Typography>
            <Typography variant="body2">{source.label}</Typography>
          </div>
        </div>
      </div>
    )
  }

  renderForm() {
    const { notes, receivedOn, source } = this.props
    return (
      <FormManager<DealInfoFormData>
        initialValues={{ notes, receivedOn, source }}
        onSubmit={this.handleSubmit}
        validateOnBlur={false}
        validateOnChange={false}
        validationSchema={validationSchema}
      >
        {(props) => this.renderInternalForm(props)}
      </FormManager>
    )
  }

  renderInternalForm({ handleSubmit }: FormProps<DealInfoFormData>) {
    const { classes } = this.props
    return (
      <div className={classes.form}>
        <FastField
          component={TextField}
          name="receivedOn"
          label="Deal received on"
          placeholder="Date where the deal was received"
          margin="normal"
          type="date"
          fullWidth
          required
          InputLabelProps={{
            shrink: true,
          }}
        />
        <FastField
          component={TextField}
          name="notes"
          label="Notes"
          multiline
          variant="outlined"
          placeholder="Place your thoughts and opinions in here"
          margin="normal"
          fullWidth
          autoFocus
        />
        <SelectFormik name="source" label="Source" options={OrganizationSourceOptions} required />
        <div className={classes.actionHandlerBottom}>
          <IconButton onClick={this.handleEditClose}>
            <KeyboardReturnIcon className={classes.actionIcon} />
          </IconButton>
          <IconButton onClick={() => handleSubmit()} color="secondary">
            <ContentSaveIcon className={classes.actionIcon} />
          </IconButton>
        </div>
      </div>
    )
  }

  render() {
    const { classes, dealId } = this.props
    const { edit } = this.state
    if (!dealId) {
      return null
    }
    return (
      <div className={classes.section}>
        <Typography variant="h6" paragraph>
          Deal Information
        </Typography>
        {edit ? this.renderForm() : this.renderData()}
      </div>
    )
  }
}

export type DealInfoProps = DealInfoFormData & {
  deal: Deal | undefined
  dealId?: Deal['id']
  onSubmit: (id: Deal['id'], dataInfo: DealInfoFormData) => void
}

type DealInfoPropsStyled = WithStyles<typeof styles> & DealInfoProps

type DealInfoState = {
  edit: boolean
}

const validationSchema = Yup.object().shape({
  notes: Yup.string(),
  receivedOn: Yup.date().required('The deal must have a valid creation date'),
})

export const styles = ({ spacing }: Theme) =>
  createStyles({
    section: {
      position: 'relative',
      marginTop: spacing(2),
      marginBottom: spacing(4),
    },
    form: {
      marginTop: -spacing(),
    },
    input: {
      paddingBottom: spacing(4),
    },
    fieldWrapper: {
      marginTop: 0,
    },
    fieldSection: {
      marginBottom: spacing(2),
    },
    fieldSectionName: {
      marginBottom: spacing(),
    },
    actionIcon: {
      width: 20,
      height: 20,
    },
    actionHandlerTop: {
      position: 'absolute',
      top: -10,
      right: 0,
    },
    actionHandlerBottom: {
      display: 'flex',
      justifyContent: 'flex-end',
    },
    notes: {
      whiteSpace: 'pre-wrap',
    },
  })
